import { apiClient } from './api';

/**
 * Service for retrieving persisted assets and sessions from the backend
 * Supports both session-based and asset-based agents
 */

export interface GeneratedAsset {
  id: string;
  userId: string;
  agentType: string;
  sessionId?: string;
  assetType: string;
  subType?: string;
  textContent?: string;
  mediaUrl?: string;
  storageUrl?: string; // Image/video storage URL
  assetUrl?: string; // NEW: Backend uses this field for image URLs
  inlineData?: string; // Inline data
  payloadJson?: string; // Payload JSON
  metadataJson?: string; // Metadata as JSON string
  metadata?: Record<string, any>;
  model?: string; // AI model used
  provider?: string; // Provider (fal.ai, etc.)
  seed?: number; // Generation seed
  promptUsed?: string; // Prompt used for generation
  tags?: string[];
  status: string | number; // Can be number (enum) or string
  createdAt: string;
  updatedAt?: string;
  completedAt?: string; // Completion timestamp
  processingStartedAt?: string; // Processing start
  retryCount?: number; // Retry count
  maxRetries?: number; // Max retries
  errorMessage?: string | null; // Error message
  estimatedCost?: number | null; // Cost estimate
  processingTimeMs?: number | null; // Processing time
  priority?: number; // Priority level
}

export interface AgentSession {
  id: string;
  userId: string;
  agentType: string;
  sessionType: string;
  title?: string | null;
  concept?: string | null;
  genre?: string | null;
  style?: string | null;
  status: string;
  metadata?: Record<string, any>;
  sessionData?: string; // JSON string containing session-specific data
  createdAt: string;
  updatedAt?: string;
  lastUpdatedAt?: string;
  lastAccessedAt?: string;
  expiresAt?: string | null;
}

export interface SessionWithAssets {
  session: AgentSession;
  assets: GeneratedAsset[];
  assetCount: number;
  iterations?: { count: number; items: any[] };
}

export interface UserSessionsResponse {
  userId: string;
  agentType: string;
  sessionCount: number;
  totalAssets: number;
  sessions: SessionWithAssets[];
}

export interface AssetStats {
  totalAssets: number;
  byType: Array<{ type: string; count: number }>;
  byAgent: Array<{ agentType: string; count: number }>;
}

export interface SessionStats {
  totalSessions: number;
  byAgentType: Array<{ agentType: string; count: number; assetCount: number }>;
  activeSessionCount: number;
  completedSessionCount: number;
}

export type AssetType =
  | 'Text'
  | 'Image'
  | 'Video'
  | 'Audio'
  | 'NovelChapter'
  | 'Screenplay'
  | 'ComicPanel'
  | 'ImagePrompt'
  | 'VideoPrompt'
  | 'MusicPrompt'
  | 'BlogPost'
  | 'Article'
  | 'SocialMediaPost';

/**
 * Asset Retrieval Service
 * Centralized service for fetching persisted assets and sessions
 */
export class AssetRetrievalService {
  /**
   * Get all sessions for a user by agent type (Session-Based Agents)
   * This method now fetches full session details with assets for each session
   */
  static async getUserSessionsByAgent(
    userId: string,
    agentType: string,
    options?: {
      status?: string;
      limit?: number;
    }
  ): Promise<SessionWithAssets[]> {
    const params = new URLSearchParams();
    if (options?.status) params.append('status', options.status);
    if (options?.limit) params.append('limit', options.limit.toString());

    // First, get the list of sessions (session IDs with basic metadata)
    const response = await apiClient.get(
      `/api/sessions/user/${userId}/agent/${agentType}?${params.toString()}`
    );

    const sessionsList = response.data.sessions || [];

    // Then, fetch full session details with assets for each session
    const sessionDetailsPromises = sessionsList.map(async (sessionMetadata: any) => {
      try {
        // Call /api/sessions/{sessionId} to get full details with assets
        const sessionId = sessionMetadata.id;
        const sessionDetailsResponse = await apiClient.get(`/api/sessions/${sessionId}`);
        const sessionData = sessionDetailsResponse.data;

        // Normalize assets to always be an array
        const normalizedAssets = Array.isArray(sessionData.assets)
          ? sessionData.assets
          : (sessionData.assets?.items || []);

        return {
          ...sessionData,
          assets: normalizedAssets,
          assetCount: sessionData.assets?.count || normalizedAssets.length,
        } as SessionWithAssets;
      } catch (error) {
        console.error(`Failed to fetch details for session ${sessionMetadata.id}:`, error);
        // Return a partial SessionWithAssets object with empty assets
        return {
          session: sessionMetadata,
          assets: [],
          assetCount: 0,
        } as SessionWithAssets;
      }
    });

    const sessionsWithAssets = await Promise.all(sessionDetailsPromises);
    return sessionsWithAssets;
  }

  /**
   * Get all sessions with assets for a user (comprehensive view)
   */
  static async getUserSessionsWithAssets(
    userId: string,
    options?: {
      agentType?: string;
      includeAssets?: boolean;
      limit?: number;
    }
  ): Promise<UserSessionsResponse> {
    const params = new URLSearchParams();
    if (options?.agentType) params.append('agentType', options.agentType);
    if (options?.includeAssets !== undefined) {
      params.append('includeAssets', options.includeAssets.toString());
    }
    if (options?.limit) params.append('limit', options.limit.toString());

    const response = await apiClient.get(
      `/api/sessions/user/${userId}/sessions-with-assets?${params.toString()}`
    );

    return response.data;
  }

  /**
   * Get a specific session with all its assets
   */
  static async getSession(sessionId: string): Promise<SessionWithAssets> {
    const response = await apiClient.get(`/api/sessions/${sessionId}`);
    return response.data;
  }

  /**
   * Get all user sessions (without assets)
   */
  static async getUserSessions(
    userId: string,
    options?: {
      agentType?: string;
      status?: string;
      limit?: number;
    }
  ): Promise<AgentSession[]> {
    const params = new URLSearchParams();
    if (options?.agentType) params.append('agentType', options.agentType);
    if (options?.status) params.append('status', options.status);
    if (options?.limit) params.append('limit', options.limit.toString());

    const response = await apiClient.get(
      `/api/sessions/user/${userId}?${params.toString()}`
    );

    return response.data;
  }

  /**
   * Get Fashion Design sessions with full metadata and assets
   */
  static async getFashionDesignSessions(
    userId: string,
    options?: {
      limit?: number;
    }
  ): Promise<any> {
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', options.limit.toString());

    const response = await apiClient.get(
      `/api/sessions/user/${userId}/fashion-designs?${params.toString()}`
    );

    return response.data;
  }

  /**
   * Search media assets (images and videos) with advanced filtering
   * Uses the new /api/assets/my-media/search endpoint
   */
  static async searchMediaAssets(
    userId: string,
    options?: {
      searchPrompt?: string;
      agentTypes?: string[];
      assetTypes?: string[];
      page?: number;
      pageSize?: number;
      sortBy?: 'createdAt' | 'completedAt';
      sortOrder?: 'asc' | 'desc';
    }
  ): Promise<any> {
    const params = new URLSearchParams();

    // Optional prompt search
    if (options?.searchPrompt) {
      params.append('prompt', options.searchPrompt);
    }

    // Filter by agent types (comma-separated)
    if (options?.agentTypes && options.agentTypes.length > 0) {
      params.append('agentTypes', options.agentTypes.join(','));
    }

    // Filter by asset types (comma-separated)
    if (options?.assetTypes && options.assetTypes.length > 0) {
      params.append('assetTypes', options.assetTypes.join(','));
    }

    // Pagination
    if (options?.page) {
      params.append('page', options.page.toString());
    }
    if (options?.pageSize) {
      params.append('pageSize', options.pageSize.toString());
    }

    // Sorting
    if (options?.sortBy) {
      params.append('sortBy', options.sortBy);
    }
    if (options?.sortOrder) {
      params.append('sortOrder', options.sortOrder);
    }

    const response = await apiClient.get(
      `/api/assets/my-media/search?${params.toString()}`
    );

    return response.data;
  }

  /**
   * Get all assets for a user (Asset-Based Agents)
   */
  static async getUserAssets(
    userId: string,
    options?: {
      assetType?: AssetType;
      agentType?: string;  // Added: Filter by agent type
      limit?: number;
    }
  ): Promise<GeneratedAsset[]> {
    const params = new URLSearchParams();
    if (options?.assetType) params.append('assetType', options.assetType);
    if (options?.agentType) params.append('agentType', options.agentType);  // Added: Pass agentType to API
    if (options?.limit) params.append('limit', options.limit.toString());

    const response = await apiClient.get(
      `/api/assets/user/${userId}?${params.toString()}`
    );

    // Check if response is wrapped in an object
    if (response.data && !Array.isArray(response.data)) {
      // Check for sessions array structure
      if (response.data.sessions && Array.isArray(response.data.sessions)) {
        // Flatten assets from all sessions
        const allAssets = response.data.sessions.flatMap((session: any) => {
          // Check if assets is an object with items array
          if (session.assets && typeof session.assets === 'object') {
            if (Array.isArray(session.assets.items)) {
              return session.assets.items;
            } else if (Array.isArray(session.assets)) {
              return session.assets;
            }
          }
          return [];
        });
        return allAssets;
      }

      // Check for assets object with items array (new structure)
      if (response.data.assets && typeof response.data.assets === 'object') {
        if (Array.isArray(response.data.assets.items)) {
          return response.data.assets.items;
        } else if (Array.isArray(response.data.assets)) {
          return response.data.assets;
        }
      }
    }

    // Fallback: return as-is if it's already an array
    return response.data;
  }

  /**
   * Get assets for a specific session
   */
  static async getSessionAssets(sessionId: string): Promise<GeneratedAsset[]> {
    const response = await apiClient.get(`/api/assets/session/${sessionId}`);

    // Check if response is wrapped in an object with assets array
    if (response.data && !Array.isArray(response.data)) {
      if (response.data.assets && Array.isArray(response.data.assets)) {
        return response.data.assets;
      }
    }

    return response.data;
  }

  /**
   * Get a specific asset by ID
   */
  static async getAsset(assetId: string): Promise<GeneratedAsset> {
    const response = await apiClient.get(`/api/assets/${assetId}`);
    return response.data;
  }

  /**
   * Download an asset (for media files)
   */
  static async downloadAsset(assetId: string): Promise<Blob> {
    const response = await apiClient.get(`/api/assets/${assetId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  }

  /**
   * Export session assets in various formats
   */
  static async exportSessionAssets(
    sessionId: string,
    format: 'json' | 'markdown' | 'pdf' = 'json'
  ): Promise<Blob> {
    const response = await apiClient.get(
      `/api/assets/session/${sessionId}/export?format=${format}`,
      {
        responseType: 'blob',
      }
    );
    return response.data;
  }

  /**
   * Get asset statistics for a user
   */
  static async getUserAssetStats(userId: string): Promise<AssetStats> {
    const response = await apiClient.get(`/api/assets/user/${userId}/stats`);
    return response.data;
  }

  /**
   * Get session statistics for a user
   */
  static async getUserSessionStats(userId: string): Promise<SessionStats> {
    const response = await apiClient.get(`/api/sessions/user/${userId}/stats`);
    return response.data;
  }

  /**
   * Get available agent types from the system
   */
  static async getAgentTypes(): Promise<string[]> {
    const response = await apiClient.get('/api/sessions/agent-types');
    return response.data;
  }

  /**
   * Filter assets by agent type (client-side filtering for asset-based agents)
   */
  static filterAssetsByAgentType(
    assets: GeneratedAsset[],
    agentType: string
  ): GeneratedAsset[] {
    return assets.filter((asset) => asset.agentType === agentType);
  }

  /**
   * Sort assets by creation date
   */
  static sortAssetsByDate(
    assets: GeneratedAsset[],
    order: 'asc' | 'desc' = 'desc'
  ): GeneratedAsset[] {
    return [...assets].sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return order === 'desc' ? dateB - dateA : dateA - dateB;
    });
  }

  /**
   * Group assets by asset type
   */
  static groupAssetsByType(assets: GeneratedAsset[]): Record<string, GeneratedAsset[]> {
    return assets.reduce((groups, asset) => {
      const type = asset.assetType;
      if (!groups[type]) {
        groups[type] = [];
      }
      groups[type].push(asset);
      return groups;
    }, {} as Record<string, GeneratedAsset[]>);
  }

  /**
   * Get recent assets (last N items)
   */
  static getRecentAssets(
    assets: GeneratedAsset[],
    count: number = 10
  ): GeneratedAsset[] {
    return this.sortAssetsByDate(assets, 'desc').slice(0, count);
  }

  /**
   * Merge local storage data with backend data (for migration)
   * Useful when transitioning from local-only to backend persistence
   */
  static mergeLocalAndBackendAssets(
    localAssets: any[],
    backendAssets: GeneratedAsset[]
  ): GeneratedAsset[] {
    // Create a map of backend assets by ID
    const backendMap = new Map(backendAssets.map(asset => [asset.id, asset]));

    // Filter local assets that aren't in backend
    const localOnly = localAssets.filter(local => !backendMap.has(local.id));

    // Convert local-only assets to GeneratedAsset format (if needed)
    // This is a placeholder - implement based on your local storage structure
    const convertedLocal: GeneratedAsset[] = localOnly.map(local => ({
      id: local.id,
      userId: local.userId || '',
      agentType: local.agentType || '',
      assetType: local.assetType || 'Text',
      textContent: local.content || local.text || '',
      metadata: local.metadata || {},
      status: 'Completed',
      createdAt: local.createdAt || new Date().toISOString(),
    }));

    // Combine and sort by date
    return this.sortAssetsByDate([...backendAssets, ...convertedLocal]);
  }
}

export default AssetRetrievalService;
